import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Maximize2, Clock, Lock, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=900&q=70";

const formatINR = (n) => {
  const num = Math.round(Number(n) || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 2)} L`;
  return `₹${num.toLocaleString("en-IN")}`;
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
};

export default function ListingsPreview({ content }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/listings");
        const list = Array.isArray(data) ? data : data.items || [];
        setItems(list.slice(0, 6));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section id="listings" className="section">
      <div className="container-z">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-xl">
            <div className="label-mono mb-3">Live on Zumroo</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A231E]">
              Fresh <span className="text-[#C86646]">owner listings</span> in Pune.
            </h2>
            <p className="mt-3 text-base text-[#5C6B62] max-w-md">
              A preview of what brokers see inside the app. Unlock owner contacts for{" "}
              {content?.price_basic || "₹49"} / {content?.price_premium || "₹79"}.
            </p>
          </div>
          <Link
            to="/listings"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E3F33] hover:text-[#C86646] transition"
            data-testid="listings-see-all"
          >
            See all listings
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {loading && (
            <div className="col-span-full flex items-center justify-center py-16 text-[#5C6B62]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading listings…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="col-span-full text-center text-[#5C6B62] py-16">
              No listings to preview right now.
            </div>
          )}
          {items.map((l, i) => {
            const img = (l.images && l.images[0]) || PLACEHOLDER;
            const fee = l.listing_type === "Sale" ? "₹79" : "₹49";
            const isNew =
              l.created_at && Date.now() - new Date(l.created_at).getTime() < 7 * 86400 * 1000;
            return (
              <motion.div
                key={l.id || i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
              >
                <Link
                  to={`/listing/${l.id}`}
                  className="block zcard overflow-hidden"
                  data-testid={`listing-card-${l.id || i}`}
                >
                <div className="relative">
                  <img
                    src={img}
                    alt=""
                    className="w-full h-44 object-cover bg-[#E4E1D9]"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#C86646] text-white text-[10px] font-bold tracking-wider uppercase">
                    {l.listing_type || "Sale"}
                  </span>
                  <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[rgba(30,63,51,0.85)] text-white text-[10px] font-semibold flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {l.city || "Pune"}
                  </span>
                  {isNew && (
                    <span className="absolute top-12 left-3 px-2.5 py-1 rounded-full bg-[#2D6A4F] text-white text-[10px] font-bold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Recently Added
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-2xl font-bold text-[#1A231E] tracking-tight">
                      {formatINR(l.price)}
                      {l.listing_type === "Rent" && (
                        <span className="text-xs text-[#5C6B62] font-medium">/mo</span>
                      )}
                    </div>
                    {l.human_id && (
                      <span className="text-[10px] font-bold tracking-wider bg-[#EAF1ED] text-[#1E3F33] px-2 py-0.5 rounded">
                        {l.human_id}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-[#1A231E] font-medium truncate">
                    {l.property_type}
                    {l.bedrooms ? ` · ${l.bedrooms} BHK` : ""} in {l.locality}
                    {l.society_name && <span className="text-[#1E3F33] font-semibold"> · {l.society_name}</span>}
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-[#5C6B62]">
                    <Maximize2 className="w-3 h-3" />
                    <span>{l.area_sqft} sq ft</span>
                    <span className="w-1 h-1 rounded-full bg-[#E4E1D9]" />
                    <Clock className="w-3 h-3" />
                    <span>{timeAgo(l.created_at)}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 bg-[#FBEFE9] text-[#C86646] px-3 py-2 rounded-lg text-xs font-semibold">
                    <Lock className="w-3.5 h-3.5" />
                    Owner contact hidden · 1st view free, then {fee}
                  </div>
                </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
